import { useState } from 'react'
import { FlaskConical } from 'lucide-react'
import { Button, ConfirmDialog } from '@/components/ui'
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin'
import {
  useLoadSetupDemoData,
  useSetupWizardStatus,
} from '@/features/setup-wizard/hooks/useSetupWizard'
import type { SetupWizardStatus } from '@/types/setup-wizard.types'

/**
 * Offers sample data to an organization admin, only where the server allows
 * it (anywhere outside production; in production only for demo
 * organizations) and only until it has been loaded.
 */
export function LoadDemoDataButton(): React.ReactElement | null {
  const isOrgAdmin = useIsOrgAdmin()
  const status = useSetupWizardStatus(undefined, { enabled: isOrgAdmin })
  const loadDemoData = useLoadSetupDemoData()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const data = status.data as SetupWizardStatus | undefined
  const demoData = data?.demoData
  if (!isOrgAdmin || !demoData?.allowedHere || demoData.loaded) return null
  // An empty workspace already gets DemoDataBanner on the same page.
  if ((data?.counts?.employees ?? 2) <= 1) return null

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setConfirmOpen(true)}
        loading={loadDemoData.isPending}
      >
        <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
        <span className="ml-1.5">Load demo data</span>
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => loadDemoData.mutate(false, { onSettled: () => setConfirmOpen(false) })}
        title="Load demo data?"
        message="This adds a Demo Operations company with three sample employees, so you can try shifts, attendance and payroll. Sample employees are active users and count as seats. It only works before you add real employees."
        confirmLabel="Load demo data"
        confirmLoading={loadDemoData.isPending}
      />
    </>
  )
}

LoadDemoDataButton.displayName = 'LoadDemoDataButton'
